import { theme, ui } from "aaa";
import * as React from "react";
import * as ReactDOM from "react-dom";

interface NativeTooltipProps {
    anchorEl: HTMLElement | null;
    "aria-label"?: string;
    content: React.ReactNode;
    offset?: number;
    open: boolean;
    placement?: "top" | "bottom" | "left" | "right";
    style?: React.CSSProperties;
}

export const NativeTooltip = ({
    anchorEl,
    "aria-label": ariaLabel,
    content,
    offset = 8,
    open,
    placement = "top",
    style = {},
}: NativeTooltipProps) => {
    const tooltipRef = React.useRef<HTMLDivElement>(null);

    // Position the tooltip based on the anchor element
    React.useEffect(() => {
        if (!open || !anchorEl || !tooltipRef.current) {
            return
        };

        const anchorRect = anchorEl.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        let top = 0;
        let left = 0;

        switch (placement) {
            case "top":
                top = anchorRect.top - tooltipRect.height - offset;
                left = anchorRect.left + (anchorRect.width - tooltipRect.width) / 2;
                break;
            case "bottom":
                top = anchorRect.bottom + offset;
                left = anchorRect.left + (anchorRect.width - tooltipRect.width) / 2;
                break;
            case "left":
                top = anchorRect.top + (anchorRect.height - tooltipRect.height) / 2;
                left = anchorRect.left - tooltipRect.width - offset;
                break;
            case "right":
                top = anchorRect.top + (anchorRect.height - tooltipRect.height) / 2;
                left = anchorRect.right + offset;
                break;
        }

        // Ensure the tooltip stays within viewport
        const viewport = {
            width: window.innerWidth,
            height: window.innerHeight
        };

        if (left < 0) {
            left = 0;
        }
        if (left + tooltipRect.width > viewport.width) {
            left = viewport.width - tooltipRect.width;
        }
        if (top < 0) {
            top = 0;
        }
        if (top + tooltipRect.height > viewport.height) {
            top = viewport.height - tooltipRect.height;
        }

        tooltipRef.current.style.top = `${top}px`;
        tooltipRef.current.style.left = `${left}px`;
    }, [open, anchorEl, placement, offset, content]);

    const tooltipContent = (
        <div
            aria-hidden={!open}
            aria-label={ariaLabel}
            ref={tooltipRef}
            role="tooltip"
            style={{
                backgroundColor: ui.isDarkMode ? theme.themeColors.d_grey_300 : "#fff",
                border: `0.5px solid ${ui.isDarkMode ? theme.themeColors.d_grey_100 : "#0A285C"}`,
                borderRadius: 8,
                color: ui.isDarkMode ? theme.themeColors.d_grey_100 : "#0A285C",
                fontSize: 14,
                maxWidth: "300px",
                margin: "0 8px",
                opacity: open ? 1 : 0,
                padding: "8px 12px",
                pointerEvents: open ? "auto" : "none",
                position: "fixed",
                transition: "opacity 0.15s ease-in-out",
                zIndex: 9999,
                ...style
            }}
        >
            {content}
        </div>
    );

    const portalRoot = document.getElementById("tooltip-root");
    if (portalRoot) {
        return ReactDOM.createPortal(tooltipContent, portalRoot);
    } else {
        return tooltipContent;
    }
};


// WEBPACK FOOTER //
// ./src/components/common/NativeTooltip.tsx